import type { FC } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { config } from '@skytech/config'
import { LoaderCircle, MessagePopup, Tooltip } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { MAX_DECIMAL_12_4, MIN_PRICE } from '@skytech/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { FormikProps } from 'formik'
import { Formik, Form as FormikForm } from 'formik'
import DeleteOutlineIcon from 'mdi-react/DeleteOutlineIcon'
import UndoIcon from 'mdi-react/UndoIcon'
import { useNavigate } from 'react-router-dom'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { LoadingError } from '@/components/LoadingError'
import Tabs from '@/components/Tabs'
import { UnsavedChangesButton } from '@/components/UnsavedChangesButton'
import { ADMIN_ROLES, USER_ROLE, useHasRoleBasedAccess } from '@/hooks/access'
import { useSearchParamsByKey } from '@/hooks/searchParams'
import queries from '@/modules/admin/queries'
import type { UpdateAssetPayload } from '@/modules/admin/resources'
import adminResources from '@/modules/admin/resources'
import { commonEcomPolicyResources } from '@/resources/ecom-policy'
import { leasingResources } from '@/resources/leasing'
import { commonQueries } from '@/resources/queries'
import { isEndDateLater } from '@/util/formatters/time'

import { AssetOrigin } from '../AssetOrigin'
import { EndOfLease } from '../EndOfLease'
import { getCostObjectById, getInitialValues } from '../helpers'
import type { FormFields } from '../types'

import { Asset } from './Asset'
import { LeasingInfo } from './LeasingInfo'
import { UserInfo } from './UserInfo'

const isDevEnv = config.ENVIRONMENT === 'dev'

export const Form: FC<{
  assetId: number
  onChangeUserTypeId: (userTypeId: number) => void
}> = ({ assetId, onChangeUserTypeId }) => {
  const formRef = useRef<FormikProps<FormFields> | null>(null)
  const hasLocationsAccess = useHasRoleBasedAccess([...ADMIN_ROLES, USER_ROLE.IT_USER])

  const [isDeleteOpen, setIsDeleteOpen] = useState(false)

  const navigate = useNavigate()

  const queryClient = useQueryClient()
  const translate = useTranslation()

  const [saveError, setSaveError] = useState('')
  const saveAssetMutation = useMutation(adminResources.updateAsset, {
    onError(error: { text: string; response: Response; json: Object }) {
      setSaveError(error.response.statusText)
    },
    onSettled() {
      queryClient.invalidateQueries(queries.adminAsset(assetId))
      queryClient.invalidateQueries(queries.adminAssetList(), { refetchType: 'all' })
    },
  })
  const { data: customers, isError: isCustomerError } = useQuery(
    queries.adminCustomers(),
    adminResources.getCustomers
  )
  const { data: employees, isError: isEmployeesError } = useQuery(queries.adminEmployeeList(), () =>
    adminResources.getEmployees(hasLocationsAccess)
  )
  const { data: assetDetails, isError: isAssetDetailsError } = useQuery(
    queries.adminAsset(assetId),
    () => adminResources.getAsset(assetId)
  )
  const { data: availablePolicies } = useQuery(
    commonQueries.policyList(),
    commonEcomPolicyResources.getList
  )

  // TODO: enable for production
  const { data: leasingAssetDetails, isError: isLeasingAssetDetailsError } = useQuery(
    queries.adminLeasingAssetDetails(assetId),
    () => leasingResources.getLeasingAssetDetails(assetId),
    { enabled: isDevEnv && Boolean(assetDetails) && assetDetails?.isLeased }
  )

  const activePolicies = availablePolicies?.filter(policy => policy.isActive) || []
  const isEcomPoliciesAvailable = activePolicies.length > 0

  const deleteAsset = async (id: number) => {
    if (formRef.current?.values) {
      await onSaveAsset(formRef.current?.values)
    }
    await adminResources.deleteAsset(id)
  }

  const activateAsset = async (id: number) => {
    if (formRef.current?.values) {
      await onSaveAsset(formRef.current?.values)
    }
    await adminResources.activateAsset(id)
  }

  const onSaveAsset = async (formSubmittedValues: FormFields) => {
    const { ecomPolicyId, ...formValues } = formSubmittedValues
    if (!formRef.current?.dirty) return

    if (!formValues.customerId) throw Error('Customer id missing')

    if (isEcomPoliciesAvailable && !ecomPolicyId)
      throw Error('Ecom policies are available but none was selected')

    // We display username and phone number but send only the username
    const costObject = employees && getCostObjectById(employees, formValues.costObjectId)

    const payload: UpdateAssetPayload = {
      ...formValues,
      userName: costObject?.name ?? '',
      costObjectId: formValues.costObjectId,
      customerId: formValues.customerId,
      ecomPolicyId: Number(ecomPolicyId),
      cost: Number(formValues.cost),
    }
    saveAssetMutation.mutate({ id: assetId, payload })
  }

  const { mutate: mutateDelete, isLoading: isDeleteLoading } = useMutation(deleteAsset, {
    onSuccess: () => {
      queryClient.invalidateQueries(queries.adminAssetList(), { refetchType: 'all' })
      queryClient.invalidateQueries(queries.adminAsset(assetId))
      navigate('/hardware')
    },
  })

  const { mutate: mutateActivate, isLoading: isActivateLoading } = useMutation(activateAsset, {
    onSuccess: () => {
      queryClient.invalidateQueries(queries.adminAssetList(), { refetchType: 'all' })
      queryClient.invalidateQueries(queries.adminAsset(assetId))
      navigate('/hardware')
    },
  })

  const assetDetailsTabs = useMemo(() => {
    const tabs = [
      {
        value: 'user-info',
        label: (
          <div id="inactive-tab-asset">
            {isEcomPoliciesAvailable && !formRef.current?.values.ecomPolicyId && (
              <Tooltip
                data-tooltip-id="inactive-tab-asset"
                content={translate('Ecom policy is mandatory')}
              />
            )}
            <Translation className="capitalize" id="User info" />
          </div>
        ),
      },

      {
        value: 'asset',
        label: <Translation className="capitalize" id="Asset" />,
      },
      {
        value: 'asset-origin',
        label: (
          <div id="inactive-tab-origin">
            {isEcomPoliciesAvailable && !formRef.current?.values.ecomPolicyId && (
              <Tooltip
                anchorId="inactive-tab-origin"
                content={translate('Ecom policy is mandatory')}
              />
            )}
            <Translation className="capitalize" id="Origin" />
          </div>
        ),
      },
    ]

    if (assetDetails?.isLeased) {
      tabs.splice(2, 0, {
        value: 'leasing-info',
        label: <Translation className="capitalize" id="Leasing info" />,
      })
    }

    // TODO: enable for production
    if (isDevEnv && assetDetails?.isLeased && leasingAssetDetails?.currentEndOfLeaseWorkflow) {
      tabs.splice(3, 0, {
        value: 'end-of-lease',
        label: <Translation className="capitalize" id="End of lease" />,
      })
    }
    return tabs
  }, [
    assetDetails?.isLeased,
    leasingAssetDetails?.currentEndOfLeaseWorkflow,
    isEcomPoliciesAvailable,
    translate,
  ])

  const [queryTab, setQueryTab] = useSearchParamsByKey('tab')
  const [tabFilter, setTabFilter] = useState(queryTab ?? assetDetailsTabs[0].value)

  useEffect(() => {
    setTabFilter(queryTab ?? assetDetailsTabs[0].value)
  }, [assetDetailsTabs, queryTab, tabFilter])

  // TODO: enable for production
  if (
    isEmployeesError ||
    isCustomerError ||
    isAssetDetailsError ||
    (isDevEnv && assetDetails && assetDetails.isLeased && isLeasingAssetDetailsError)
  ) {
    return <LoadingError />
  }

  if (!assetDetails || !employees || !customers) {
    return <LoaderCircle />
  }

  const hasNoCost = assetDetails.cost === 0 || assetDetails.cost === null

  const lockedFields = assetDetails.isLeased ? leasingAssetDetails?.leasingFieldLocks : undefined
  let newLockedFieldsList = assetDetails.isLeased ? leasingAssetDetails?.lockFields : undefined

  return (
    <>
      <Formik<FormFields>
        enableReinitialize
        innerRef={formRef}
        validateOnBlur={false}
        initialValues={getInitialValues(assetDetails, employees)}
        validate={values => {
          const errors: Partial<Record<keyof FormFields, string>> = {}

          if (
            (!lockedFields?.assetCustomer || !newLockedFieldsList?.includes('customerId')) &&
            !values.customerId
          ) {
            errors.customerId = translate('This field is mandatory')
          } else if (
            (!lockedFields?.assetCustomer || !newLockedFieldsList?.includes('customerId')) &&
            values.customerId &&
            !customers?.find(customer => customer.id === values.customerId)
          ) {
            errors.customerId = translate('Please choose one of the available companies')
          }
          // We should show only employees that belong to the selected customer
          if (
            (!lockedFields?.assetUser || !newLockedFieldsList?.includes('assetUser')) &&
            values.costObjectId &&
            values.customerId &&
            getCostObjectById(employees, values.costObjectId)?.customerId !== values.customerId
          ) {
            errors.costObjectId = translate('Please select one of the available employees')
          }
          if (!hasNoCost && !values.cost) {
            errors.cost = translate('This field is mandatory')
          }
          if (!hasNoCost && values.cost && Number(values.cost) > MAX_DECIMAL_12_4) {
            errors.cost = translate('The cost is too big')
          }
          if (!hasNoCost && values.cost && Number(values.cost) < MIN_PRICE) {
            errors.cost = translate('The cost is too small')
          }
          if (
            (!lockedFields?.assetDescription ||
              !newLockedFieldsList?.includes('assetDescription')) &&
            !values.assetDescription
          ) {
            errors.assetDescription = translate('This field is mandatory')
          }
          if (isEcomPoliciesAvailable && !values.ecomPolicyId) {
            errors.ecomPolicyId = translate('This field is mandatory')
          }

          if (
            (!lockedFields?.leaseStart || !newLockedFieldsList?.includes('leaseStart')) &&
            (lockedFields?.leaseEnd || newLockedFieldsList?.includes('leaseEnd')) &&
            values.leaseStart &&
            values.leaseEnd &&
            !isEndDateLater(values.leaseStart, values.leaseEnd)
          ) {
            errors.leaseStart = translate(
              'Please ensure the start date is earlier than the end date.'
            )
          }

          const leaseDateLocked =
            (lockedFields?.leaseStart || newLockedFieldsList?.includes('leaseStart')) &&
            (lockedFields?.leaseEnd || newLockedFieldsList?.includes('leaseEnd'))

          if (
            !leaseDateLocked &&
            values.leaseStart &&
            values.leaseEnd &&
            !isEndDateLater(values.leaseStart, values.leaseEnd)
          ) {
            errors.leaseEnd = translate('Please ensure the end date is later than the start date.')
          }

          return errors
        }}
        initialTouched={{
          customerId: true,
          costObjectId: true,
          assetDescription: true,
          ecomPolicyId: true,
        }}
        validateOnMount
        onSubmit={(formValues: FormFields) => onSaveAsset(formValues)}
      >
        {({ dirty, isValid }) => (
          <FormikForm>
            <Tabs
              selectedValue={tabFilter}
              tabs={assetDetailsTabs}
              onClick={tabId => {
                setQueryTab(String(tabId))
              }}
            />

            {tabFilter === 'user-info' && (
              <UserInfo assetDetails={assetDetails} onChangeUserTypeId={onChangeUserTypeId} />
            )}

            {tabFilter === 'asset' && (
              <Asset
                activePolicies={activePolicies}
                assetDetails={assetDetails}
                onChangeUserTypeId={onChangeUserTypeId}
                onSetFormValues={(updatedValues: Partial<FormFields>) =>
                  formRef.current?.setValues({ ...formRef.current.values, ...updatedValues })
                }
              />
            )}

            {tabFilter === 'leasing-info' && <LeasingInfo />}

            {/* TODO: enable for production  */}
            {isDevEnv && tabFilter === 'end-of-lease' && <EndOfLease />}

            {tabFilter === 'asset-origin' && <AssetOrigin />}

            <footer>
              {assetDetails.active ? (
                <Button
                  onClick={() => setIsDeleteOpen(true)}
                  Icon={<DeleteOutlineIcon />}
                  isLoading={isDeleteLoading || saveAssetMutation.isLoading}
                >
                  <Translation id="Delete asset" />
                </Button>
              ) : (
                <Button
                  onClick={() => mutateActivate(assetId)}
                  Icon={<UndoIcon />}
                  isLoading={isActivateLoading || saveAssetMutation.isLoading}
                >
                  <Translation id="Unhide asset" />
                </Button>
              )}

              {tabFilter !== 'asset-origin' && (
                <UnsavedChangesButton
                  error={!isValid}
                  hasUnsavedChanges={dirty}
                  isLoading={saveAssetMutation.isLoading}
                />
              )}
            </footer>
          </FormikForm>
        )}
      </Formik>

      <MessagePopup
        title={<Translation id="Are you absolutely sure?" />}
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        message={
          <div>
            <p>
              <Translation id="This action cannot be undone. This will permanently delete the selected asset" />
              : {assetDetails.assetDescription} ({assetDetails.id}).
            </p>

            <div className="flex justify-end">
              <Button variant="secondary" onClick={() => setIsDeleteOpen(false)}>
                <Translation id="Cancel" />
              </Button>

              <Button
                variant="primary"
                onClick={() => mutateDelete(assetId)}
                isLoading={isDeleteLoading}
              >
                <Translation id="Continue" />
              </Button>
            </div>
          </div>
        }
      />

      <MessagePopup
        title={<Translation id="Saving form error" />}
        message={
          <>
            <Translation id="We encountered a problem while saving your changes. Please refresh and try again." />{' '}
            <Translation id="Error" />: {saveError}
          </>
        }
        isOpen={Boolean(saveError)}
        onClose={() => setSaveError('')}
      />
    </>
  )
}
